import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Header } from "@aptsi-types";
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
} from "ag-grid-community";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { KeycloakStateService } from "src/app/core/service/utility.service";
import { DeleteDialogComponent } from "../../../shared/dialogs/delete-dialog/delete-dialog.component";
import {
  RiskRegister,
  RiskRegisterService,
} from "../../risk-register/risk-register-service";
import { RiskRegisterAssetInfoDialogComponent } from "../../risk-register/shared/dialogs/risk-register-asset-info-dialog/risk-register-asset-info-dialog.component";

@Component({
  selector: "app-risk-register-search",
  templateUrl: "./risk-register-search.component.html",
  styleUrls: ["./risk-register-search.component.scss"],
})
export class RiskRegisterSearchComponent implements OnInit {
  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── User Context ─────────────────────────────────────────────
  sessionId = this.keycloakStateService.getSessionId();
  userId = this.keycloakStateService.getUserId();

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = "";
  filterByHeader: string = "";
  inSearchMode = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  allRisks: RiskRegister[] = [];
  filteredData: RiskRegister[] = [];
  searchedData: RiskRegister[] = [];

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "Assessment Id",
      property: "id",
      showInDropdown: true,
      description: "Unique identifier for the risk assessment",
    },
    {
      name: "Assessment Name",
      property: "title",
      showInDropdown: true,
      description: "Name of the risk assessment",
    },
    {
      name: "Status",
      property: "status",
      showInDropdown: true,
      description: "Current workflow status",
    },
    {
      name: "Asset Id",
      property: "assetId",
      showInDropdown: true,
      description: "Associated asset identifier",
    },
    {
      name: "Risk",
      property: "riskValue",
      showInDropdown: true,
      description: "Calculated risk value",
    },
  ];

  buttonConfig = {
    update: true,
    delete: true,
    capture: false,
    respectManualVisibility: false,
  };

  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 200,
    filter: true,
  };

  private gridApi: GridApi | null = null;
  private isDeleting = false;

  constructor(
    private router: Router,
    private riskRegisterService: RiskRegisterService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private keycloakStateService: KeycloakStateService,
  ) {}

  // ──────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    this.buildColumnDefs();
    await this.fetchRisks();

    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.searchRisks();
      });

    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────
  // Getters
  // ──────────────────────────────────────────────────────────────
  get paginatedData(): RiskRegister[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.inSearchMode
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  get totalDisplayItems(): number {
    return this.inSearchMode
      ? this.searchedData.length
      : this.filteredData.length;
  }

  get hasTableData(): boolean {
    return this.totalDisplayItems > 0;
  }

  // ──────────────────────────────────────────────────────────────
  // Event Handlers
  // ──────────────────────────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.inSearchMode
      ? this.totalPagesSearch
      : this.totalPagesNormal;
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
  }

  onGridCellClicked(event: CellClickedEvent): void {
    if (event.column?.getColId() !== "actions" || !event.data) {
      return;
    }

    const target = event.event?.target as HTMLElement | null;
    const actionEl = target?.closest("[data-action]") as HTMLElement | null;
    if (!actionEl) {
      return;
    }

    event.event?.stopPropagation();
    event.event?.preventDefault();

    const action = actionEl.getAttribute("data-action");
    const rowData = event.data as RiskRegister;

    if (action === "update") {
      this.updateRisk(rowData);
    } else if (action === "delete") {
      this.deleteRisk(rowData);
    }
  }

  onGridRowClicked(event: RowClickedEvent): void {
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest("[data-action], .risk-register-action")) {
      return;
    }

    if (event.data) {
      this.viewAssessmentDetails(event.data as RiskRegister);
    }
  }

  private buildColumnDefs(): void {
    const dataColumns: ColDef[] = this.headers.map((header) => ({
      headerName: header.name,
      field: header.property,
    }));

    this.columnDefs = [
      {
        headerName: "Actions",
        colId: "actions",
        field: "actions",
        width: 200,
        sortable: false,
        filter: false,
        cellRenderer: () => this.getActionsCellHtml(),
      },
      ...dataColumns,
    ];
  }

  private getActionsCellHtml(): string {
    return `
      <div class="risk-register-actions-cell">
        <span class="risk-register-action" data-action="update" title="Edit">
          <i class="bi bi-pencil-fill" style="color: grey;"></i>
        </span>
        <span class="risk-register-action" data-action="delete" title="Delete">
          <i class="bi bi-trash" style="color: rgb(224, 71, 71);"></i>
        </span>
      </div>
    `;
  }

  updateRiskByIndex(index: number): void {
    const risk = this.paginatedData[index];
    if (risk) {
      this.updateRisk(risk);
    }
  }

  deleteRiskByIndex(index: number): void {
    const risk = this.paginatedData[index];
    if (risk) {
      this.deleteRisk(risk);
    }
  }

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: RiskRegister;
  }): void {
    if (event.action === "update") {
      this.updateRiskByIndex(event.rowIndex);
    } else if (event.action === "delete") {
      this.deleteRiskByIndex(event.rowIndex);
    }
  }

  viewAssessmentDetails(risk: RiskRegister): void {
    this.dialog.open(RiskRegisterAssetInfoDialogComponent, {
      width: "800px",
      maxWidth: "95vw",
      maxHeight: "90vh",
      data: { risk },
    });
  }

  // ──────────────────────────────────────────────────────────────
  // Navigation
  // ──────────────────────────────────────────────────────────────
  updateRisk(risk: RiskRegister): void {
    const riskRegisterId = String(risk.id ?? risk.assessmentId ?? "").trim();
    if (!riskRegisterId) {
      return;
    }

    this.router.navigate([
      "insight-egrc/auth-tools/risk-management/risk-register/update",
      riskRegisterId,
    ]);
  }

  createRisk(): void {
    this.router.navigate([
      "insight-egrc/auth-tools/risk-management/risk-register/create",
    ]);
  }

  calculateRisk(): void {
    this.router.navigate([
      "insight-egrc/auth-tools/risk-management/risk-register/risk-calculator",
    ]);
  }

  back(): void {
    if (this.inSearchMode) {
      this.inSearchMode = false;
      this.applyPagination();
    } else {
      this.router.navigate(["insight-egrc/auth-tools"]);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Data Fetching
  // ──────────────────────────────────────────────────────────────
  async fetchRisks(): Promise<void> {
    try {
      const response = await this.riskRegisterService
        .getRiskRegister(this.currentPage - 1, this.sessionId, this.userId)
        .toPromise();
      this.allRisks = (response ?? []).map((risk) => ({
        ...risk,
      }));
      this.inSearchMode = false;
      this.applyPagination();
    } catch (error) {
      console.error("Error fetching Risk:", error);
    }
  }

  private applyPagination(): void {
    this.filteredData = [...this.allRisks];
    this.totalPagesNormal = Math.max(
      1,
      Math.ceil(this.filteredData.length / this.itemsPerPage),
    );
    this.totalPages = this.totalPagesNormal;
    this.currentPage = Math.min(this.currentPage, this.totalPagesNormal);
  }

  // ──────────────────────────────────────────────────────────────
  // Search
  // ──────────────────────────────────────────────────────────────
  async searchRisks(): Promise<void> {
    try {
      if (this.searchText) {
        this.inSearchMode = true;

        const selectedHeader = this.headers.find(
          (h) => h.property === this.filterByHeader && h.showInDropdown,
        );
        if (selectedHeader?.property) {
          const key = selectedHeader.property as keyof RiskRegister;
          this.searchedData = this.allRisks.filter((risk) => {
            const value = risk[key];
            return value?.toString().toLowerCase().includes(
              this.searchText.toLowerCase(),
            );
          });
        } else {
          this.searchedData = this.allRisks.filter((risk) =>
            this.headers.some((header) => {
              const value = risk[header.property];
              return value?.toString().toLowerCase().includes(
                this.searchText.toLowerCase(),
              );
            }),
          );
        }

        this.totalPagesSearch = Math.max(
          1,
          Math.ceil(this.searchedData.length / this.itemsPerPage),
        );
        this.totalPages = this.totalPagesSearch;
        this.currentPage = 1;
      } else {
        this.inSearchMode = false;
        this.applyPagination();
      }
    } catch (error) {
      console.error("Error during search:", error);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Delete
  // ──────────────────────────────────────────────────────────────
  async deleteRisk(risk: RiskRegister): Promise<void> {
    const riskRegisterId = String(risk?.id ?? risk?.assessmentId ?? "").trim();
    if (!riskRegisterId) {
      return;
    }

    if (this.isDeleting) {
      return;
    }

    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      disableClose: true,
      data: {
        title: "Are you sure?",
        message: "Do you really want to delete this Risk Assessment?",
        isConfirm: false,
      },
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    this.isDeleting = true;
    try {
      await new Promise<void>((resolve, reject) => {
        this.riskRegisterService.deleteRiskRegisterl(riskRegisterId).subscribe({
          next: () => resolve(),
          error: (error) => reject(error),
        });
      });

      await this.fetchRisks();
      if (this.inSearchMode) {
        await this.searchRisks();
      } else {
        this.applyPagination();
      }

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Success",
          message: "The Risk Assessment has been deleted successfully.",
          isConfirm: true,
        },
      });
    } catch (error) {
      console.error("Error deleting Risk:", error);
    } finally {
      this.isDeleting = false;
    }
  }
}
